<script setup lang="ts">
import { useDestinationStore } from '@/store/DestinationStore';
import AutoComplete from 'primevue/autocomplete';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import { FilterMatchMode, FilterService } from '@primevue/core/api';
import { computed, ref, watch } from 'vue';
import { TabMixins } from '@/components/tabs/mixin';
import { useDurationStore } from '@/store/DurationStore';
import { useHotelStore } from '@/store/HotelStore';
import { DateTime } from 'luxon';
import { getCurrentLocale } from '@/i18n';
import { useTabComposable } from '../useTabComposable';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { LANG_ENGLISH } from '@/constants';
import { useCruiseStore } from '@/store/CruiseStore';
import CruiseHotelRoomSelection from './CruiseHotelRoomSelection.vue';

interface Props {
  type: 'pre' | 'post';
  hotels: any;
  cruiseIndexOption: number;
  isShowError: boolean;
  isAvancedForm: boolean;
  advancedForm: object;
  instanceForm?: object;
  minDateRangeValue: Date;
  maxDateRangeValue?: Date;
  adults: number;
  validate: object;
  calendarData?: object;
  maxHotelConfig?: number | null;
  maxRoomConfig?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  maxHotelConfig: 1,
  maxRoomConfig: 1
});
const emits = defineEmits<{
  'update:hotels': [value: any];
  addRoom: [];
  removeRoom: [];
  addHotel: [];
  removeHotel: [];
}>();

const { hotelRoomPayload } = useTabComposable();
const hotelStore = useHotelStore();
const durationStore = useDurationStore();
const destinationStore = useDestinationStore();
const viewQuoteStore = useViewQuoteStore();
const cruiseStore = useCruiseStore();

const currentLanguage = getCurrentLocale();
const filterDestinationPreHotel = ref<any>(destinationStore.selectDestinationsOnlyHotel);
const isRetDate = ref(false);
const maxDateRangeHotel = ref(props.maxDateRangeValue);

const hotels = computed({
  get() {
    return props.hotels;
  },
  set(value) {
    emits('update:hotels', value);
  }
});
const destinationsOnlyHotel = computed(() => {
  return destinationStore.selectDestinationsOnlyHotel;
});
const isRequote = computed(() => viewQuoteStore.selectIsRequote);

const multipleOnlyHotelIndex = computed(() => props.cruiseIndexOption * 2 + (props.type === 'pre' ? 0 : 1));

const searchHotelDestination = (event: any) => {
  const query = event.query;
  const newFilteredDestination = [];

  for (const destination of destinationsOnlyHotel.value) {
    const filteredItems = FilterService.filter(destination.items, ['name', 'nameFr', 'airportCode', 'country', 'countryFr'], query, FilterMatchMode.CONTAINS);
    if (filteredItems?.length) {
      newFilteredDestination.push({ ...destination, ...{ items: filteredItems } });
    }
  }
  filterDestinationPreHotel.value = TabMixins.handleDuplicate(newFilteredDestination);
};

const selectDestinationHotel = (destination: any, index: any, option: any) => {
  durationStore.updateDurationsMultiple({ durations: [], index });
  hotelStore.updateHotelMultiple({ hotels: [], index });

  const durationList = destination?.durations;
  if (durationList?.length > 0) {
    const durations = durationList.map((item: any) => {
      return { name: item };
    });
    durationStore.updateDurationsMultipleOnlyHotel({ durations, index });
  }

  if (destination?.code && option.duration) {
    getHotelList(destination?.code, index, option);
  }
};

const getHotelList = async (destinationId: any, hotelIndex: any, hotel: any) => {
  const payloadHotel = {
    quoteType: 'HOTEL',
    duration: hotel?.duration,
    departDate: hotel?.dateRange?.[0]
  };
  if (!destinationId) {
    return;
  }
  const listHotelId = await hotelStore.getListHotelFormDestination(destinationId);
  if (listHotelId.data?.length) {
    await hotelStore.getHotelMultipleOnlyHotel({ hotelDurations: listHotelId.data, index: hotelIndex, data: payloadHotel });
  }
  return listHotelId;
};

const changeDateRange = (e: any, index: any, option: any) => {
  if (e) {
    let diffDays = DateTime.fromJSDate(new Date(e[1]))
      .diff(DateTime.fromJSDate(new Date(e[0])), 'days')
      .toObject().days;
    diffDays = Math.ceil(diffDays!);
    isRetDate.value = !hotels.value[index].dateRange[1];
    const addDate = new Date(DateTime.fromJSDate(new Date(e[0])).plus({ days: 14 }));
    getMaxDate(addDate, index, hotels.value[index].dateRange);
    if (diffDays + 1 < 0) {
      hotels.value[index].duration = 0;
      return;
    }
    if (isNaN(diffDays) || diffDays === 0) {
      hotels.value[index].duration = 1;
    } else {
      hotels.value[index].duration = diffDays;
    }
    hotelChangeRequote(option, index);
    if (option?.destination?.code && option.duration) {
      getHotelList(option?.destination?.code, multipleOnlyHotelIndex.value, option);
    }
  }
};

const getMaxDate = (date: any, index: number, selectedDateHotel: any) => {
  maxDateRangeHotel.value = selectedDateHotel[1] ? null : new Date(date);
};

const hotelChangeRequote = async (e: any, index: number) => {
  if (!e.hotel[0]?.hotelName?.hCode || !isRequote.value) {
    return;
  }
  if (hotels.value[index].dateRange.length && hotels.value[index].dateRange[1] && hotels.value[index].adults) {
    const payload = {
      dateDep: DateTime.fromJSDate(new Date(hotels.value[index].dateRange[0])).toFormat('yyyyLLdd'),
      dateRet: DateTime.fromJSDate(new Date(hotels.value[index].dateRange[1])).toFormat('yyyyLLdd'),
      duration: hotels.value[index].duration || '',
      destDep: hotels.value[index].destination.airportCode || '',
      productCode: e.hotel[0].hotelName.hCode,
      language: currentLanguage.toUpperCase(),
      nbAdult: +hotels.value[index].adults,
      ...hotelRoomPayload
    };
    hotelStore.updateRoomLoading(true);
    await hotelStore.getHotelRoomCategory(payload, e.hotel[0]?.hotelName?.excludeHotelRoom);
    hotelStore.updateRoomLoading(false);
  }
};

const resetDateRangeDuration = (index: number) => {
  hotels.value[index].dateRange = [];
  hotels.value[index].duration = null;
};

const resetHotelOptions = (index: number) => {
  hotels.value.splice(1);
  hotels.value[index].hotel.splice(0);
  hotels.value[index].hotel.push(cruiseStore.initHotelData());
};

const resetHotelRoomOptions = (indexOption: number, indexHotel: number) => {
  hotels.value[indexOption].hotel[indexHotel].rooms.splice(0);
  addRoom(indexOption, indexHotel);
};

const changeAdult = (option: any, index: number) => {
  hotelChangeRequote(option, index);
};

const clearDestination = (index: number) => {
  hotels.value[index].destination = '';
  resetDateRangeDuration(index);
  resetHotelOptions(index);
};

const removeHotel = (indexOption: number) => {
  hotels.value[indexOption].hotel.pop();
  emits('removeHotel');
};

const addHotel = (indexOption: number) => {
  hotels.value[indexOption].hotel.push(cruiseStore.initHotelData());
  emits('addHotel');
};

const removeRoom = (indexOption: number, indexHotel: number) => {
  hotels.value[indexOption].hotel[indexHotel].rooms.pop();
  emits('removeRoom');
};

const addRoom = (indexOption: number, indexHotel: number) => {
  hotels.value[indexOption].hotel[indexHotel].rooms.push({
    room: ''
  });
  emits('addRoom');
};

watch(
  () => hotels.value[0].destination,
  () => {
    resetDateRangeDuration(0);
    resetHotelOptions(0);
  },
  {
    deep: true
  }
);

watch(
  () => hotels.value[0].dateRange,
  (newValue) => {
    resetHotelOptions(0);
    if (!newValue) {
      maxDateRangeHotel.value = props.maxDateRangeValue;
    }
  },
  {
    deep: true
  }
);
</script>

<template>
  <div :class="`cruise-${type}-hotel-form`">
    <div class="m-2 text-3xl font-bold uppercase mt-4">{{ $t(type === 'pre' ? 'create_quote.pre_hotel' : 'create_quote.post_hotel') }}</div>
    <div class="mb-3">
      <div class="mb-3">{{ $t('advance_quote.form.hotel_desc') }}</div>
      <span class="text-primary font-bold">{{ $t('advance_quote.sign') }}</span>
    </div>
    <div v-for="(option, index) in hotels" :key="index">
      <Fluid>
        <div class="flex align-items-center">
          <div class="field w-full">
            <label for="dest">{{ $t('advance_quote.form.destination') }}</label>
            <div class="flex align-items-center">
              <AutoComplete
                v-model="option.destination"
                :suggestions="filterDestinationPreHotel"
                @complete="searchHotelDestination($event)"
                optionLabel="name"
                optionGroupLabel="label"
                optionGroupChildren="items"
                completeOnFocus
                optionValue="name"
                @change="selectDestinationHotel(option.destination, multipleOnlyHotelIndex, option)"
                :class="isShowError && !option.destination ? 'p-invalid' : ''"
                class="w-full"
                forceSelection
              >
                <template #option="slotProps">
                  <div class="ml-7">
                    {{ currentLanguage === LANG_ENGLISH ? `${slotProps.option.name} (${slotProps.option.country})` : `${slotProps.option.nameFr} (${slotProps.option.countryFr})` }}
                  </div>
                </template>
              </AutoComplete>
              <div>
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearDestination(index)"></i>
              </div>
            </div>
            <span class="text-red-500" v-if="isShowError && !option.destination"> {{ $t('validate.required') }} </span>
          </div>
        </div>
      </Fluid>
      <div class="grid">
        <div class="col-12 md:col-6">
          <Fluid>
            <div class="field">
              <label for="dest">{{ $t('advance_quote.form.date_range') }}</label>
              <DatePicker
                v-model="option.dateRange"
                dateFormat="dd-M-yy"
                selectionMode="range"
                @update:modelValue="changeDateRange($event, index, option)"
                :minDate="minDateRangeValue"
                :maxDate="maxDateRangeHotel"
                showButtonBar
                :selectOtherMonths="true"
                :class="isShowError && !option.dateRange.length ? 'p-invalid' : ''"
                :placeholder="$t('common.date_format_range')"
              />
              <span class="text-red-500" v-if="isShowError && !option.dateRange.length"> {{ $t('validate.required') }} </span>
              <br />
              <span class="text-red-500" v-if="isRetDate">{{ $t('create_quote.required_return_date') }}</span>
            </div>
          </Fluid>
        </div>
        <div class="col-12 md:col-6">
          <Fluid>
            <div class="field">
              <label for="duration">{{ $t('advance_quote.form.duration') }}</label>
              <InputText id="duration" v-model="option.duration" option-label="name" :disabled="true" :placeholder="$t('advance_quote.form.duration')" />
            </div>
          </Fluid>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6 flex gap-2 flex-wrap sm:flex-nowrap">
          <Fluid class="w-full sm:w-6">
            <div class="field">
              <label for="adt" class="long-text">{{ $t('advance_quote.form.adult') }}</label>
              <InputNumber :min="0" id="adt" type="text" v-model="option.adults" :class="isShowError && !option.adults ? 'p-invalid' : ''" @update:modelValue="changeAdult(option, index)" />
              <span class="text-red-500" v-if="isShowError && !option.adults"> {{ $t('validate.required') }} </span>
            </div>
          </Fluid>
          <Fluid class="w-full sm:w-6">
            <div class="field">
              <label for="chd" class="long-text">{{ $t('advance_quote.form.children') }}</label>
              <InputNumber :min="0" id="chd" type="text" v-model="option.children" />
            </div>
          </Fluid>
        </div>
        <div class="col-12 md:col-6">
          <Fluid>
            <div class="field">
              <label for="number_room" class="long-text" v-tooltip.top="$t('advance_quote.form.number_of_room')">{{ $t('advance_quote.form.number_of_room') }}</label>
              <InputNumber :min="0" id="number_room" :class="isShowError && !option.numOfRooms ? 'p-invalid' : ''" type="text" v-model="option.numOfRooms" />
              <span class="text-red-500" v-if="isShowError && !option.numOfRooms"> {{ $t('validate.required') }} </span>
            </div>
          </Fluid>
        </div>
      </div>

      <div class="hotel-select">
        <CruiseHotelRoomSelection
          :instanceForm="instanceForm"
          :advancedForm="advancedForm"
          v-model:hotels="hotels"
          :minDateRangeValue="minDateRangeValue"
          :maxDateRangeValue="maxDateRangeValue!"
          :hotelOptionConfig="maxHotelConfig"
          :roomOptionConfig="maxRoomConfig"
          :hotelOption="option"
          :indexOption="index"
          :isShowError="isShowError"
          :validate="validate"
          :multipleOnlyHotelIndex="multipleOnlyHotelIndex"
          @addRoom="(indexOption, indexHotel) => addRoom(indexOption, indexHotel)"
          @removeRoom="(indexOption, indexHotel) => removeRoom(indexOption, indexHotel)"
          @addHotel="(indexOption) => addHotel(indexOption)"
          @removeHotel="(indexOption) => removeHotel(indexOption)"
          @resetHotelRoom="(indexOption, indexHotel) => resetHotelRoomOptions(indexOption, indexHotel)"
        />
      </div>
    </div>
  </div>
</template>
